/**
 * @namespace GeoLocationService
 */

import { Browser } from "./helpers/Browser";
import IPService from "./IPService";

const GeoLocationService = {
    /**
     * Determines if access should be allowed based on IP, country, region, and bot detection.
     *
     * Rules:
     * - If no restrictions are provided, access is allowed.
     * - If the IP is in the allowed list, access is granted immediately.
     * - If the user-agent matches a known bot, access is granted.
     * - If only countries are specified, a matching country allows access.
     * - If both countries and regions are specified, both must match.
     *
     * @param {Object} restrictions
     * @param {string[]} [restrictions.allowedCountries] - List of allowed country codes (e.g. 'US', 'DE').
     * @param {string[]} [restrictions.allowedRegions] - List of allowed region names (e.g. 'California').
     * @param {string[]} [restrictions.allowedIps] - List of allowed IP addresses.
     * @param {string} country - The user’s country code.
     * @param {string} region - The user’s region name.
     * @param {string} ip - The user’s IP address.
     * @returns {boolean} `true` if access is allowed, `false` otherwise.
     */
    isAccessAllowed(restrictions, country, region, ip) {
        // Handle null/undefined restrictions as "no restrictions"
        if (!restrictions) return true;

        const {
            restrictedCountriesWithStates = {},
            whiteListedIps = []
        } = restrictions;

        // No restrictions: allow
        if (!restrictedCountriesWithStates && (!whiteListedIps || whiteListedIps.length === 0)) {
            return true;
        }

        // Bot check
        const userAgent = Browser.getUserAgentString();
        const isBot = Browser.isCrawelerBot(userAgent);
        if (isBot) return true;

        // Whitelisted IPs
        if (typeof ip === 'string' && IPService.ipMatchesList(ip, whiteListedIps)) {
            return true;
        }

        if (country && typeof country === 'string') {
            const countryLower = country?.toLowerCase();
            // Find matching country key (case-insensitive)
            const matchedCountryKey = Object.keys(restrictedCountriesWithStates)
                .find(key => key.toLowerCase() === countryLower);
            if (matchedCountryKey) {
                const restrictedRegions = restrictedCountriesWithStates[matchedCountryKey];
                // If regions list is empty, restrict all regions in this country
                if (!Array.isArray(restrictedRegions) || restrictedRegions.length === 0) {
                    return false;
                }
                if (region && typeof region === 'string') {
                    const regionLower = region?.toLowerCase();
                    // Check if any region matches case-insensitively
                    if (restrictedRegions.some(r => typeof r === 'string' && r.toLowerCase() === regionLower)) {
                        return false;
                    }
                }
            }
        }

        // If not restricted, allow access
        return true;
    }
};

export default GeoLocationService;
